<template>
  <div>
    <record_loader v-if="loading" />
    <div v-else class="primary-container">
      <h1 class="red_color">Submit Timesheets for {{ company_name }}</h1>

      <div v-if="server_error" class="vvERROR">
        Could not load submission periods. Please try again.
      </div>

      <AdminModeContext v-else v-slot="{ adminActive, toggleAdmin }">
        <div class="advanced_options_header">
          <AdminToggleButton
              :active="adminActive"
              :toggle-admin="toggleAdmin"
          />
        </div>

        <div class="period_sections">
          <!-- --- Future / Current Cycle --- -->
          <section v-if="adminActive" class="period_section">
            <h4 class="text-warning mb-3">Future / Current Cycle Periods (hidden from client)</h4>
            <ValidationObserver ref="futureForm" slim>
              <form class="period_form" role="form" @submit.prevent="enter_new_ts(selectedFuture)">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="select_period_form_inputs">
                    <select
                        v-model="selectedFuture"
                        class="form-control form-control-lg"
                        :disabled="no_dates || futureWeeks.length === 0"
                    >
                      <option v-for="date in futureWeeks" :key="'f-' + date.start_date" :value="date">
                        {{ dateRange(date.start_date, date.end_date) }}
                      </option>
                      <option v-if="futureWeeks.length === 0" disabled :value="null">
                        No future/current periods available
                      </option>
                    </select>

                    <button
                        class="btn orcaBtn"
                        type="submit"
                        :disabled="no_dates || !selectedFuture"
                    >
                      View Period
                    </button>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </form>
            </ValidationObserver>
          </section>

          <!-- --- In Progress / Unsubmitted --- -->
          <section class="period_section">
            <h4 class="text-primary mb-3">In Progress / Unsubmitted Periods (visible to client/workers)</h4>
            <ValidationObserver ref="unsubmittedForm" slim>
              <form class="period_form" role="form" @submit.prevent="enter_new_ts(selectedUnsubmitted)">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="select_period_form_inputs">
                    <select
                        v-model="selectedUnsubmitted"
                        class="form-control form-control-lg"
                        :disabled="no_dates || unsubmittedWeeks.length === 0"
                    >
                      <option v-for="date in unsubmittedWeeks" :key="'u-' + date.start_date" :value="date">
                        {{ dateRange(date.start_date, date.end_date) }}
                      </option>
                      <option v-if="unsubmittedWeeks.length === 0" disabled :value="null">
                        No in-progress periods available
                      </option>
                    </select>

                    <button
                        class="btn orcaBtn"
                        type="submit"
                        :disabled="no_dates || !selectedUnsubmitted"
                    >
                      Select Period
                    </button>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </form>
            </ValidationObserver>
          </section>

          <!-- --- Watermark Suppressed --- -->
          <section v-if="adminActive" class="period_section">
            <h4 class="text-warning mb-3">Client Watermark Suppressed Periods (hidden from client)</h4>
            <ValidationObserver ref="watermarkSuppressedForm" slim>
              <form class="period_form" role="form" @submit.prevent="enter_new_ts(selectedWatermarkSuppressed)">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="select_period_form_inputs">
                    <select
                        v-model="selectedWatermarkSuppressed"
                        class="form-control form-control-lg"
                        :disabled="no_dates || watermarkSuppressedWeeks.length === 0"
                    >
                      <option v-for="date in watermarkSuppressedWeeks" :key="'w-' + date.start_date" :value="date">
                        {{ dateRange(date.start_date, date.end_date) }}
                      </option>
                      <option v-if="watermarkSuppressedWeeks.length === 0" disabled :value="null">
                        No suppressed periods available
                      </option>
                    </select>

                    <button
                        class="btn orcaBtn"
                        type="submit"
                        :disabled="no_dates || !selectedWatermarkSuppressed"
                    >
                      View Period
                    </button>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </form>
            </ValidationObserver>
          </section>

          <!-- --- Submitted --- -->
          <section class="period_section">
            <h4 class="text-success mb-3">Submitted Periods</h4>
            <ValidationObserver ref="submittedForm" slim>
              <form class="period_form" role="form" @submit.prevent="enter_new_ts(selectedSubmitted)">
                <ValidationProvider v-slot="{ errors }" rules="required">
                  <div class="select_period_form_inputs">
                    <select
                        v-model="selectedSubmitted"
                        class="form-control form-control-lg"
                        :disabled="no_dates || submittedWeeks.length === 0"
                    >
                      <option v-for="date in submittedWeeks" :key="'s-' + date.start_date" :value="date">
                        {{ dateRange(date.start_date, date.end_date) }} (Submitted)
                      </option>
                      <option v-if="submittedWeeks.length === 0" disabled :value="null">
                        No submitted periods available
                      </option>
                    </select>

                    <button
                        class="btn orcaBtn"
                        type="submit"
                        :disabled="no_dates || !selectedSubmitted"
                    >
                      View Period
                    </button>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </form>
            </ValidationObserver>
          </section>
        </div>

        <!-- --- Client watermark (admin only, kept apart from the period pickers) --- -->
        <div v-if="adminActive" class="watermark_panel">
            <section class="period_section">
              <h4 class="text-warning mb-3">Update Client Watermark</h4>

              <p>
                Current watermark:
                <strong>{{ watermark.current ? dateFormat(watermark.current) : 'none' }}</strong>.
                <span v-if="watermark.current">
                  Periods on or before this are treated as closed for the client.
                </span>
                <span v-else>
                  Nothing is being suppressed from the client.
                </span>
              </p>

              <p v-if="watermark.unsubmitted_weeks_on_or_before > 0" class="text-warning">
                {{ watermark.unsubmitted_weeks_on_or_before }} still-open period(s) sit on or before the
                watermark and are therefore hidden from the client.
              </p>

              <div class="select_period_form_inputs">
                <input
                    v-model="watermark.input"
                    type="date"
                    class="form-control form-control-lg"
                    :disabled="watermark.saving"
                />

                <button
                    class="btn orcaBtn"
                    type="button"
                    :disabled="watermark.saving || !watermark.input"
                    title="Set the watermark to this date"
                    @click="saveWatermark({ weekending_date: watermark.input })"
                >
                  {{ watermark.saving ? 'Saving ...' : 'Set date' }}
                </button>
              </div>

              <div class="watermark_actions">
                <button
                    class="btn orcaBtn"
                    type="button"
                    :disabled="watermark.saving"
                    title="Detect from paid cycles and reapply the default"
                    @click="saveWatermark({ mode: 'auto' })"
                >
                  Auto detect
                </button>

                <button
                    class="btn orcaBtn"
                    type="button"
                    :disabled="watermark.saving || !watermark.current"
                    title="Remove the watermark so nothing is suppressed from the client"
                    @click="saveWatermark({ weekending_date: '' })"
                >
                  Remove watermark
                </button>
              </div>

              <p v-if="watermark.error" class="text-danger mt-2">{{ watermark.error }}</p>
              <p v-else-if="watermark.notice" class="text-muted mt-2">{{ watermark.notice }}</p>
              <small class="text-muted">
                Moving the watermark back, or removing it, re-exposes periods to the client. Changes are
                recorded in the timesheet activity log.
              </small>
            </section>
        </div>

        <!-- --- Worker period visibility (admin only, read-only diagnostic) --- -->
        <div v-if="adminActive" class="watermark_panel">
          <section class="period_section">
            <h4 class="text-warning mb-3">Worker Period Visibility</h4>

            <p>
              Shows the periods the selected worker can currently see in their own
              period picker (spans every company the worker has timesheets with).
            </p>

            <div class="select_period_form_inputs">
              <select
                  v-model="workerVisibility.selectedWorkerId"
                  class="form-control form-control-lg"
                  :disabled="workerVisibility.loadingWorkers || workerVisibility.workers.length === 0"
              >
                <option :value="null" disabled>
                  {{ workerVisibilityPlaceholder }}
                </option>
                <option v-for="worker in workerVisibility.workers" :key="worker.id" :value="worker.id">
                  {{ worker.name }} (#{{ worker.id }})
                </option>
              </select>

              <button
                  class="btn orcaBtn"
                  type="button"
                  :disabled="!workerVisibility.selectedWorkerId || workerVisibility.loading"
                  @click="fetchWorkerVisibleWeeks"
              >
                {{ workerVisibility.loading ? 'Loading ...' : 'View' }}
              </button>
            </div>

            <div v-if="workerVisibility.loaded">
              <p v-if="workerVisibility.weeks.length === 0" class="text-muted worker_visibility_result">
                This worker currently sees no periods.
              </p>
              <ul v-else class="worker_visibility_result">
                <li v-for="week in workerVisibility.weeks" :key="week.api_date">
                  {{ week.select_date_string }}
                </li>
              </ul>
            </div>

            <p v-if="workerVisibility.error" class="text-danger mt-2">{{ workerVisibility.error }}</p>
          </section>
        </div>
      </AdminModeContext>
    </div>
  </div>
</template>

<script>
import record_loader from "loaders/order_loader.vue";
import AdminModeContext from "pages/sunset/timesheets/components/shared/AdminModeContext.vue";
import AdminToggleButton from "pages/sunset/timesheets/components/shared/AdminToggleButton.vue";

export default {
  name: "admin_timesheet_select_period",
  components: {
    AdminModeContext,
    AdminToggleButton,
    record_loader,
  },
  props: {
    company_id: { required: true },
  },
  data() {
    return {
      loading: false,
      submissionWeeks: [],
      futureWeeks: [],
      unsubmittedWeeks: [],
      watermarkSuppressedWeeks: [],
      watermark: {
        current: null,
        unsubmitted_weeks_on_or_before: 0,
        input: '',
        saving: false,
        error: '',
        notice: '',
      },
      workerVisibility: {
        workers: [],
        selectedWorkerId: null,
        weeks: [],
        loaded: false,
        loading: false,
        loadingWorkers: false,
        error: '',
      },
      submittedWeeks: [],
      selectedFuture: null,
      selectedUnsubmitted: null,
      selectedWatermarkSuppressed: null,
      selectedSubmitted: null,
      company_name: null,
      timesheet_cycle_id: null,
      timesheet_template_name: null,
      no_dates: false,
      server_error: false,
    };
  },
  async beforeMount() {
    await this.getData();
    await this.fetchWatermark();
    await this.fetchWorkersWithTimesheets();
  },
  computed: {
    workerVisibilityPlaceholder() {
      if (this.workerVisibility.loadingWorkers) return 'Loading workers ...';
      if (this.workerVisibility.workers.length === 0) return 'No workers with timesheets';

      return 'Select a worker';
    },
  },
  methods: {
    async fetchWorkersWithTimesheets() {
      this.workerVisibility.loadingWorkers = true;

      try {
        const res = await axios.get(
            `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/workers_with_timesheets`
        );

        this.workerVisibility.workers = res.data.workers || [];
      } catch (e) {
        this.workerVisibility.error = 'Could not load the worker list.';
      } finally {
        this.workerVisibility.loadingWorkers = false;
      }
    },

    async fetchWorkerVisibleWeeks() {
      if (!this.workerVisibility.selectedWorkerId) return;

      this.workerVisibility.loading = true;
      this.workerVisibility.error = '';
      this.workerVisibility.loaded = false;

      try {
        const res = await axios.get(
            `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/worker_visible_weeks/${this.workerVisibility.selectedWorkerId}`
        );

        this.workerVisibility.weeks = res.data.data || [];
        this.workerVisibility.loaded = true;
      } catch (e) {
        this.workerVisibility.error = 'Could not load the worker view.';
      } finally {
        this.workerVisibility.loading = false;
      }
    },

    watermarkPath() {
      return `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/submission_watermark`;
    },

    async fetchWatermark() {
      try {
        const res = await axios.get(this.watermarkPath());

        this.watermark.current = res.data.client_submitted_watermark || null;
        this.watermark.unsubmitted_weeks_on_or_before = Number(res.data.unsubmitted_weeks_on_or_before) || 0;
        this.watermark.input = this.watermark.current || '';
      } catch (e) {
        // The period lists must still work if the watermark cannot be read.
        this.watermark.error = 'Could not read the current watermark.';
      }
    },

    // payload is { weekending_date } to set, { weekending_date: '' } to remove,
    // or { mode: 'auto' } to detect. Reloads the period lists afterwards because
    // moving the mark changes which periods are suppressed.
    async saveWatermark(payload) {
      this.watermark.saving = true;
      this.watermark.error = '';
      this.watermark.notice = '';

      try {
        const res = await axios.post(this.watermarkPath(), payload);

        this.watermark.current = res.data.client_submitted_watermark || null;
        this.watermark.unsubmitted_weeks_on_or_before = Number(res.data.unsubmitted_weeks_on_or_before) || 0;
        this.watermark.input = this.watermark.current || '';

        if (payload.mode === 'auto' && Number(res.data.seeded) === 0) {
          this.watermark.notice = this.watermark.current
              ? 'Nothing detected - the existing watermark was kept.'
              : 'Nothing to detect - no paid cycles found for this company.';
        }

        await this.fetchWatermark();
        await this.getData();
      } catch (e) {
        this.watermark.error = e?.response?.data?.message || 'Could not update the watermark.';
      } finally {
        this.watermark.saving = false;
      }
    },

    dateFormat(date) {
      return this.$moment.utc(date).format("DD/MM/YYYY");
    },
    dateRange(d1, d2) {
      return this.$moment.utc(d1).format("DD/MM/YYYY") + " - " + this.$moment.utc(d2).format("DD/MM/YYYY");
    },
    isTruthyFlag(value) {
      return value === true
          || value === 1
          || value === "1"
          || value === "yes"
          || value === "true";
    },
    isFutureWeek(week) {
      return this.isTruthyFlag(week.is_future_cycle);
    },
    isWatermarkSuppressedWeek(week) {
      return this.isTruthyFlag(week.past_client_submitted_watermark);
    },
    enter_new_ts(selectedPeriod) {
      if (!selectedPeriod) return;

      this.$router.push({
        name: "a_timesheet_r2_select_sites",
        params: {
          company_id: this.company_id,
          end_date: selectedPeriod.end_date,
          start_date: selectedPeriod.start_date,
        },
      });
    },
    async getData() {
      this.loading = true;
      this.server_error = false;
      this.no_dates = false;

      try {
        const company = await axios.get(this.crmApiPath + "company/" + this.company_id);

        this.timesheet_template_name = company.data.timesheet_template_name;
        this.company_name = company.data.company_name;
        this.timesheet_cycle_id = company.data.rel_payment_process_info
            ? company.data.rel_payment_process_info.timesheet_cycle_id
            : null;

        const result = await axios.get(
            `${this.crmApiPath}r3/admin/timesheets/company/${this.company_id}/submission_weeks?limit=all`
        );

        if (result.status !== 200) {
          this.server_error = true;
          return;
        }

        const allWeeks = result.data.weeks || [];

        this.submissionWeeks = allWeeks;

        this.futureWeeks = allWeeks.filter(w => this.isFutureWeek(w));

        this.watermarkSuppressedWeeks = allWeeks.filter(w =>
            !this.isFutureWeek(w) &&
            this.isWatermarkSuppressedWeek(w) &&
            w.submitted === "no"
        );

        this.unsubmittedWeeks = allWeeks.filter(w =>
            !this.isFutureWeek(w) &&
            !this.isWatermarkSuppressedWeek(w) &&
            w.submitted === "no"
        );

        this.submittedWeeks = allWeeks.filter(w =>
            !this.isFutureWeek(w) &&
            w.submitted === "yes"
        );

        this.no_dates = allWeeks.length === 0;

        this.selectedFuture = this.futureWeeks.length > 0 ? this.futureWeeks[0] : null;
        this.selectedUnsubmitted = this.unsubmittedWeeks.length > 0 ? this.unsubmittedWeeks[0] : null;
        this.selectedWatermarkSuppressed = this.watermarkSuppressedWeeks.length > 0 ? this.watermarkSuppressedWeeks[0] : null;
        this.selectedSubmitted = this.submittedWeeks.length > 0 ? this.submittedWeeks[0] : null;
      } catch (e) {
        console.error("Error loading submission weeks", e);
        this.server_error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
div.primary-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: flex-start;
  width: 100%;
  height: 100%;
  overflow: auto;
}

div.advanced_options_header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  width: 100%;
  margin: 0 0 24px;
}

div.advanced_options_header > * {
  justify-self: end;
}

div.period_sections {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 34px;
  width: 100%;
}

div.watermark_actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

/* Kept visually separate from the period pickers above: this one writes. */
div.watermark_panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  margin-top: 34px;
  padding-top: 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.125);
}

section.period_section {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: flex-start;
  max-width: 620px;
}

form.period_form {
  display: block;
  margin: 0;
}

button.orcaBtn {
  width: auto;
  max-width: fit-content;
}

div.select_period_form_inputs {
  display: grid;
  grid-template-columns: minmax(260px, 330px);
  align-content: flex-start;
  justify-content: flex-start;
  grid-gap: 10px;
}

h4 {
  font-weight: bold;
  font-size: 1.1rem;
}

ul.worker_visibility_result,
p.worker_visibility_result {
  margin: 12px 0 0;
}

ul.worker_visibility_result {
  padding-left: 18px;
}

ul.worker_visibility_result li {
  line-height: 1.6;
}
</style>